/**
 * Admin (admin only) — the single consolidated admin panel.
 *
 * One screen, nine tabs, each owning its own query/save wiring and the same
 * dirty/saved/error/loading UX:
 *   1. Users & Access  — provision staff/managers + RingCentral extensions +
 *      soft deactivate/reactivate.
 *   2. Cost — spend dashboard (today + 7-day trend, category breakdown with
 *      tokens/seconds, top-N expensive calls). ADMIN-ONLY server routes.
 *   3. Capture health — live supervise quality (both-legs %, rep drops,
 *      failure reasons, recent per-leg outcomes).
 *   4. After-call grader — model + base prompt + per-type guidance + rubric
 *      editor + the bulk "Re-score all" (estimate → confirm → run).
 *   5. Classifier — model + prompt + editable staff roster.
 *   6. Value-builders — approved USPs + review thresholds + exclusions.
 *   7. Live coaching — nudge master switch + model + prompt + cadence +
 *      reference cards, PLUS the live-CAPTURE controls (billing kill-switch,
 *      daily spend-guard thresholds, capture hours) and the auto-halt banner.
 *   8. Call-log privacy — the admin-managed phone-number blocklist (hide calls
 *      from the reviewer for staff + managers; admins always see them; the live
 *      coach is unaffected).
 *   9. Audit log — read-only viewer of recent AI runs (model/tokens/cost/status +
 *      👍/👎 counts) with prompt/response expansion (CLAUDE.md §8).
 *
 * Replaces the former separate "Users" (/admin → AdminUsers) and "AI Settings"
 * (/ai-settings → AiSettings) screens. The /admin route is admin-guarded in
 * main.tsx; /ai-settings now redirects here (preserving a `?tab=` if present).
 *
 * The active tab is reflected in `?tab=` so old /ai-settings links land on the
 * right tab and the URL is shareable.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/ui";
import { AdminUsers } from "./AdminUsers";
import { CostTab } from "./CostTab";
import { CaptureHealthTab } from "./CaptureHealthTab";
import { GraderTab } from "./aiSettings/GraderTab";
import { ClassifierTab } from "./aiSettings/ClassifierTab";
import { ValueBuildersTab } from "./aiSettings/ValueBuildersTab";
import { LiveCoachingTab } from "./aiSettings/LiveCoachingTab";
import { CallLogPrivacyTab } from "./aiSettings/CallLogPrivacyTab";
import { AuditLogTab } from "./aiSettings/AuditLogTab";
import { SettingsTab } from "./aiSettings/SettingsTab";

type TabId =
  | "users"
  | "cost"
  | "capture"
  | "grader"
  | "classifier"
  | "value_builders"
  | "live"
  | "operations"
  | "advanced"
  | "privacy"
  | "audit";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "users", label: "Users & Access" },
  // Cost + Capture health up front — the daily "is it healthy / what's it
  // costing" glance comes before the (less frequently touched) AI knobs.
  { id: "cost", label: "Cost" },
  { id: "capture", label: "Capture health" },
  { id: "grader", label: "After-call grader" },
  { id: "classifier", label: "Classifier" },
  { id: "value_builders", label: "Value-builders" },
  { id: "live", label: "Live coaching" },
  { id: "operations", label: "Operations" },
  { id: "privacy", label: "Privacy & access" },
  { id: "audit", label: "Audit log" },
  // Advanced last — deeper tuning knobs, behind their own tab.
  { id: "advanced", label: "Advanced" },
];

function isTabId(v: string | null): v is TabId {
  return TABS.some((t) => t.id === v);
}

export function Admin() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab");
  const [tab, setTab] = useState<TabId>(isTabId(initial) ? initial : "users");

  function select(id: TabId) {
    setTab(id);
    // Keep the URL in sync so /ai-settings?tab=… deep links and refreshes work.
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  }

  return (
    <div className="flex flex-1 flex-col">
      <PageHeader
        title="Admin"
        subtitle="Users and access, cost and capture health, every AI knob, and call-log privacy."
      />

      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto border-b px-5">
        {TABS.map((t) => {
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => select(t.id)}
              aria-current={active ? "page" : undefined}
              className={
                "relative -mb-px whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition " +
                (active
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground")
              }
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "users" ? (
        // AdminUsers renders its own PageHeader + full-width layout, so it sits
        // directly under the tab bar without the shared padded wrapper.
        <AdminUsers />
      ) : (
        <div className="flex flex-1 flex-col gap-6 p-5">
          {tab === "cost" && <CostTab />}
          {tab === "capture" && <CaptureHealthTab />}
          {tab === "grader" && <GraderTab />}
          {tab === "classifier" && <ClassifierTab />}
          {tab === "value_builders" && <ValueBuildersTab />}
          {tab === "live" && <LiveCoachingTab />}
          {tab === "operations" && <SettingsTab tier={1} />}
          {tab === "advanced" && <SettingsTab tier={2} />}
          {tab === "privacy" && <CallLogPrivacyTab />}
          {tab === "audit" && <AuditLogTab />}
        </div>
      )}
    </div>
  );
}
